// /src/data/books/FundametosBlockchain/data/cap11.js
const CAP11 = {
  id: "fundamentos-blockchain-11",
  slug: "protocolos-nft",
  title: "Protocolos",
  sections: [
    {
      id: "hero",
      heading: "Protocolos para Emisión y Comercio de NFT",
      text: `Un sistema NFT funcional se apoya en un libro mayor distribuido con propiedades de seguridad, consistencia,
integridad y disponibilidad. Sobre esa base se definen roles (propietario/emisor y comprador) y un flujo de mensajes
(transacciones) que invocan contratos inteligentes para acuñar, transferir y registrar la propiedad y la procedencia.`
    },

    /* =========================
       1) ROLES Y SUPUESTOS
       ========================= */
    {
      id: "roles-y-supuestos",
      heading: "Roles y supuestos del sistema",
      list: [
        { title: "Libro mayor distribuido", text: "Red P2P con consenso y finalización de transacciones; los bloques encadenados garantizan inmutabilidad práctica." },
        { title: "Propietario/Emisor de NFT", text: "Prepara el activo, firma transacciones y autoriza acuñación/transferencias." },
        { title: "Comprador/Adquirente", text: "Envía pagos/compras, recibe la titularidad y verifica metadatos." },
        { title: "Contratos inteligentes", text: "Lógica determinista para acuñar (mint), transferir, quemar (burn) y consultar metadatos/propiedad." },
        { title: "Carteras y firmas", text: "Custodian la clave privada; autorizan operaciones mediante firmas digitales." }
      ]
    },

    /* =========================
       2) FLUJO PRINCIPAL DEL PROTOCOLO NFT
       ========================= */
    {
      id: "flujo-protocolo-nft",
      heading: "Flujo de protocolo: de archivo a NFT en cadena",
      text: "Pasos canónicos desde la preparación del activo hasta su confirmación en la blockchain.",
      list: [
        { title: "1) Digitalización del activo", text: "Verificar archivo, título, descripción y atributos; normalizar formatos (imagen/audio/video/3D)." },
        { title: "2) Preparación de metadatos", text: "Construir metadata JSON (nombre, descripción, imagen, atributos). Opcional: hash de integridad del archivo." },
        { title: "3) Almacenamiento del contenido", text: "Subir archivo y metadata a almacenamiento externo (p. ej., IPFS/Arweave) o, si se requiere, on-chain (alto costo)." },
        { title: "4) Enlace y hash", text: "Insertar el enlace (tokenURI) y, si aplica, el hash del contenido/metadatos para anclar procedencia." },
        { title: "5) Firma y envío", text: "El emisor firma una transacción que invoca el contrato (mint) incluyendo tokenURI y destinatario." },
        { title: "6) Acuñación y eventos", text: "El contrato acuña el token, registra el propietario y emite eventos (p. ej., Transfer del 0x0 al nuevo dueño)." },
        { title: "7) Confirmación en bloque", text: "La transacción se incluye en un bloque; tras confirmaciones/finalidad, el NFT queda asentado." },
        { title: "8) Disponibilidad para comercio", text: "El token puede listarse en mercados; el comprador ejecuta compra/transferencia según el protocolo del marketplace." }
      ]
    },

    /* =========================
       3) ALMACENAMIENTO Y METADATOS
       ========================= */
    {
      id: "almacenamiento-metadatos",
      heading: "Almacenamiento y metadatos",
      list: [
        { title: "Off-chain verificado", text: "Almacenar archivo y metadata fuera de cadena reduce costos; asegurar referencias inmutables (CIDs/IPFS) y hashes." },
        { title: "On-chain selectivo", text: "Para obras pequeñas/estáticas, incrustar datos en cadena maximiza resistencia a la censura (costo elevado)." },
        { title: "Buenas prácticas de metadata", text: "Estándar JSON estable; campos consistentes (nombre, descripción, imagen, atributos), y versión/esquema claro." }
      ]
    },

    /* =========================
       4) FIRMA, INTEGRIDAD Y PROCEDENCIA
       ========================= */
    {
      id: "firma-e-integridad",
      heading: "Firma, integridad y procedencia",
      list: [
        { title: "Firmas del emisor", text: "Autorizan acuñación/listados; demuestran control de la clave del propietario original." },
        { title: "Hashes de contenido", text: "Permiten verificar que la obra descargada coincide con lo referenciado por el token." },
        { title: "Línea de tiempo (provenance)", text: "Eventos y transferencias registran la historia del NFT; facilitan auditorías y autenticidad." }
      ]
    },

    /* =========================
       5) MINT & TRADE (COMPRA/VENTA)
       ========================= */
    {
      id: "mint-y-trade",
      heading: "Mint y comercio (primario y secundario)",
      text: "Interacciones estándar para acuñar y comerciar NFTs de forma segura.",
      list: [
        { title: "Acuñación primaria (mint)", text: "El contrato crea el token y asigna propietario; puede fijar precio, cupos o allowlists." },
        { title: "Listados/ventas", text: "El vendedor autoriza (approve/setApprovalForAll) o firma un listado; el comprador ejecuta la orden con pago." },
        { title: "Transferencia segura", text: "safeTransferFrom (ERC-721) o transferencias en lote (ERC-1155) validan receptores compatibles." },
        { title: "Regalías/fees", text: "Si el contrato implementa regalías, el marketplace consulta y distribuye porcentajes en cada reventa." },
        { title: "Quema/retirada", text: "burn elimina el token del suministro; debe emitir eventos y actualizar estados de propiedad." }
      ]
    },

    /* =========================
       6) CONFIRMACIÓN Y FINALIDAD
       ========================= */
    {
      id: "confirmacion-finalidad",
      heading: "Confirmación y finalidad en cadena",
      list: [
        { title: "Mempool y selección", text: "Las transacciones compiten por espacio en bloque según comisión; prioridad por tarifa y política del validador." },
        { title: "Inclusión y confirmaciones", text: "Tras incluirse en bloque, se esperan confirmaciones/epochs hasta considerar la transacción final." },
        { title: "Reorgs y resiliencia", text: "Reorganizaciones profundas son infrecuentes; la aplicación debe manejar estados intermedios con prudencia." }
      ]
    },

    /* =========================
       7) CAPACIDAD, COSTOS Y RENDIMIENTO
       ========================= */
    {
      id: "capacidad-costos",
      heading: "Capacidad de bloque, costos y rendimiento",
      list: [
        { title: "Espacio limitado por bloque", text: "Si el bloque se llena, la transacción pasa a bloques futuros; incrementos de comisión aceleran inclusión." },
        { title: "Optimización de gas", text: "Mint en lote (ERC-2309 para 721; batch en 1155), compresión de metadatos y reutilización de librerías." },
        { title: "Cadenas/L2", text: "Uso de L2 sidechains o rollups para reducir costos manteniendo seguridad o finalidad adecuada al caso." }
      ]
    },

    /* =========================
       8) OPERACIONES COMPLEMENTARIAS
       ========================= */
    {
      id: "operaciones-complementarias",
      heading: "Operaciones complementarias del ciclo de vida",
      list: [
        { title: "Aprobaciones", text: "approve/setApprovalForAll habilitan a mercados o custodios a transferir en nombre del propietario." },
        { title: "Actualizaciones controladas", text: "Si hay ‘reveal’ o metadatos mutables, documentar política de cambios y versionado." },
        { title: "Reclamaciones y airdrops", text: "Mints condicionados a pruebas (listas, firmas, códigos) o distribuciones por snapshot." }
      ]
    },

    /* =========================
       9) SEGURIDAD DEL PROTOCOLO
       ========================= */
    {
      id: "seguridad-protocolo",
      heading: "Seguridad del protocolo y del usuario",
      list: [
        { title: "Reentradas y validaciones", text: "Proteger transferencias/listados; validar emisores/receptores y no confiar en callbacks externos." },
        { title: "Firmas y phishing", text: "Mostrar con claridad lo que se firma; rechazar firmas ambiguas o con permisos excesivos." },
        { title: "Verificación del contrato", text: "Contratos verificados y auditados; ABI pública; eventos coherentes y sin puntos ciegos." }
      ]
    },

    /* =========================
       10) CONCLUSIÓN
       ========================= */
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `El protocolo NFT encadena digitalización, almacenamiento, firma, acuñación y confirmación en la blockchain.
Cada transacción agrega un eslabón verificable al historial del token, preservando propiedad y procedencia.
Con buenas prácticas en metadatos, seguridad y manejo de costos, el ciclo de vida del NFT se ejecuta de forma predecible,
interoperable y trazable en mercados primarios y secundarios.`
    }
  ]
};

export default CAP11;
